import React, { useCallback, useEffect, useState } from 'react';
import { formatDisplayInstant } from '../utils/displayDate';

const TEMP_PASSWORD = 'Welcome123!';

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return formatDisplayInstant(iso) || iso;
  } catch (_) {
    return iso;
  }
}

function AdminUsers({ deliveryApiUrl, userEmail, userToken }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  const [newEmail, setNewEmail] = useState('');
  const [creating, setCreating] = useState(false);
  const [createStatus, setCreateStatus] = useState('');

  const loadUsers = useCallback(async () => {
    if (!deliveryApiUrl || !userEmail || !userToken) return;
    setLoading(true);
    setLoadError('');
    try {
      const url =
        `${deliveryApiUrl}?action=adminListUsers&email=${encodeURIComponent(userEmail)}` +
        `&token=${encodeURIComponent(userToken)}`;
      const resp = await fetch(url);
      const data = await resp.json();
      if (data.success) {
        setUsers(data.users || []);
      } else {
        setLoadError(data.error || 'Could not load users');
      }
    } catch (err) {
      setLoadError(err.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }, [deliveryApiUrl, userEmail, userToken]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateStatus('');
    const trimmed = (newEmail || '').trim();
    if (!trimmed) {
      setCreateStatus('Please enter an email.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setCreateStatus('That doesn’t look like a valid email.');
      return;
    }
    setCreating(true);
    try {
      const resp = await fetch(deliveryApiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify({
          action: 'adminCreateUser',
          adminEmail: userEmail,
          adminToken: userToken,
          email: trimmed
        }),
        mode: 'cors'
      });
      const data = await resp.json();
      if (data.success) {
        setCreateStatus(
          `✓ Added ${data.email}. Temporary password: ${data.tempPassword || TEMP_PASSWORD}. ` +
          `Ask them to sign in and use “Forgot Your Password” to set their own.`
        );
        setNewEmail('');
        loadUsers();
      } else {
        setCreateStatus(`Could not add user: ${data.error || 'Unknown error'}`);
      }
    } catch (err) {
      setCreateStatus(`Network error: ${err.message || 'Unknown error'}`);
    } finally {
      setCreating(false);
    }
  };

  if (!deliveryApiUrl) {
    return (
      <div className="form-section">
        <p style={{ color: 'var(--brand-text-light)' }}>
          Admin panel requires REACT_APP_DELIVERY_API_URL to be configured.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="form-section">
        <h3 style={{ marginTop: 0 }}>Add User</h3>
        <p className="helper-text" style={{ marginTop: 0 }}>
          New users get the temporary password <strong>{TEMP_PASSWORD}</strong>. They’ll be
          prompted to change it on their first login (or can use “Forgot Your Password”).
        </p>
        <form onSubmit={handleCreate} style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 280px' }}>
            <label className="form-label" htmlFor="admin-new-email">
              Email address
            </label>
            <input
              id="admin-new-email"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="firstname@mayker.com"
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: 15,
                boxSizing: 'border-box',
                backgroundColor: 'white'
              }}
              disabled={creating}
            />
          </div>
          <div>
            <button
              type="submit"
              disabled={creating}
              style={{
                padding: '12px 20px',
                backgroundColor: 'var(--brand-charcoal)',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                fontSize: 14,
                fontWeight: 500,
                cursor: creating ? 'not-allowed' : 'pointer',
                opacity: creating ? 0.7 : 1,
                fontFamily: "'NeueHaasUnica', sans-serif",
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}
            >
              {creating ? 'Adding…' : 'Add User'}
            </button>
          </div>
        </form>
        {createStatus && (
          <div
            style={{
              marginTop: 16,
              padding: 12,
              borderRadius: 6,
              backgroundColor: createStatus.startsWith('✓') ? '#ecfdf5' : '#fee2e2',
              color: createStatus.startsWith('✓') ? '#065f46' : '#991b1b',
              fontSize: 14
            }}
          >
            {createStatus}
          </div>
        )}
      </div>

      <div className="form-section">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>Users</h3>
          <button
            type="button"
            onClick={loadUsers}
            disabled={loading}
            style={{
              padding: '8px 14px',
              backgroundColor: 'transparent',
              color: 'var(--brand-charcoal)',
              border: '1px solid var(--brand-light-border)',
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 500,
              cursor: loading ? 'not-allowed' : 'pointer',
              fontFamily: "'NeueHaasUnica', sans-serif",
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        {loadError && (
          <div
            style={{
              padding: 12,
              borderRadius: 6,
              backgroundColor: '#fee2e2',
              color: '#991b1b',
              fontSize: 14,
              marginBottom: 16
            }}
          >
            {loadError}
          </div>
        )}

        {!loadError && users.length === 0 && !loading && (
          <div className="helper-text">No users yet.</div>
        )}

        {users.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--brand-light-border)' }}>
                  <th style={{ padding: '10px 8px' }}>Email</th>
                  <th style={{ padding: '10px 8px' }}>Status</th>
                  <th style={{ padding: '10px 8px' }}>Last Login</th>
                  <th style={{ padding: '10px 8px' }}>Role</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.email} style={{ borderBottom: '1px solid var(--brand-light-border)' }}>
                    <td style={{ padding: '10px 8px' }}>{u.email}</td>
                    <td style={{ padding: '10px 8px' }}>{u.status || 'Active'}</td>
                    <td style={{ padding: '10px 8px', color: 'var(--brand-text-light)' }}>
                      {formatDate(u.lastLogin)}
                    </td>
                    <td style={{ padding: '10px 8px' }}>{u.isAdmin ? 'Admin' : 'User'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminUsers;
